'use client';

import { motion } from 'framer-motion';
import { SAVINGS_GOAL, savingsDip, type SavingsScale } from '@/src/shared/lib/chart';
import { cn } from '@/src/shared/lib/shadcn';

const GROW_EASE = [0.22, 1, 0.36, 1] as const;
const STAGGER_S = 0.12;
const BARS_AT_S = 0.4;
const MET = 'from-[color-mix(in_srgb,var(--s-in)_35%,transparent)] to-(--s-in)';
const MISSED = 'from-[color-mix(in_srgb,var(--s-warn)_35%,transparent)] to-(--s-warn)';
const GLOW = 'shadow-[0_0_24px_-4px_color-mix(in_srgb,var(--s-in)_60%,transparent)]';
const BAR =
  'absolute w-full max-w-10 transition-opacity duration-100 motion-reduce:transition-none';

interface Props {
  index: number;
  pct: number | null;
  scale: SavingsScale;
  /** Height of the scale above zero, in pixels. */
  plotHeight: number;
  /** The rate written over the bar; null when the column is too narrow for it. */
  value: string | null;
  latest: boolean;
  opacity: number;
  reduced: boolean;
}

/**
 * One month: a faint track up to the top of the scale, the bar grown from zero in
 * green past the goal and amber under it, and its rate over the bar. An overspent
 * month grows a short stub under the zero line; a month without income, or a slot
 * with no month, draws only the track.
 */
export function SavingsRateColumn(props: Props) {
  const { index, pct, scale, plotHeight, value, latest, opacity, reduced } = props;
  const share = (rate: number) => (rate / scale.hi) * 100;
  const delay = BARS_AT_S + index * STAGGER_S;
  const from = <T,>(start: T) => (reduced ? false : start);
  const grow = { duration: 0.6, delay, ease: GROW_EASE };
  const tip = pct !== null && pct > 0 ? share(pct) : 0;

  return (
    // The padding sits inside, so every slot shares the row's width alike.
    <div data-testid="savings-column" className="h-full min-w-0 flex-1">
      <div className="relative mx-1 flex h-full justify-center sm:mx-2">
        <span className="absolute inset-y-0 w-full max-w-10 rounded-t-lg bg-secondary/40" />
        {pct !== null && pct > 0 && (
          <motion.span
            data-testid="savings-bar"
            className={cn(
              BAR,
              'origin-bottom rounded-t-lg bg-gradient-to-t',
              pct >= SAVINGS_GOAL ? MET : MISSED,
              latest && GLOW,
            )}
            style={{ bottom: 0, height: `${tip}%`, opacity }}
            initial={from({ scaleY: 0 })}
            animate={{ scaleY: 1 }}
            transition={grow}
          />
        )}
        {pct !== null && pct < 0 && (
          <motion.span
            data-testid="savings-bar-negative"
            className={cn(BAR, 'origin-top rounded-b-lg bg-gradient-to-b', MISSED)}
            style={{ top: '100%', height: savingsDip(pct, plotHeight / scale.hi), opacity }}
            initial={from({ scaleY: 0 })}
            animate={{ scaleY: 1 }}
            transition={grow}
          />
        )}
        {value !== null && (
          <motion.span
            data-testid="savings-value"
            className="absolute text-[11px] font-semibold whitespace-nowrap tabular-nums text-foreground"
            style={{ bottom: `calc(${tip}% + 4px)` }}
            initial={from({ opacity: 0, y: 4 })}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: delay + 0.4 }}
          >
            {value}
          </motion.span>
        )}
      </div>
    </div>
  );
}
